import { ReactNode } from 'react';

import {
  IconPaymentTypeAddNewPayment,
  IconPaymentTypeAmex,
  IconPaymentTypeApplePay,
  IconPaymentTypeDinersClub,
  IconPaymentTypeDiscover,
  IconPaymentTypeGenericCreditCard,
  IconPaymentTypeGooglePay,
  IconPaymentTypeJcb,
  IconPaymentTypeMastercard,
  IconPaymentTypeVisa,
  IconRedeem,
} from '@fhs/ui';
import { CardBrand } from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import { CardTypes, IPaymentMethod } from '@fhs-legacy/frontend/src/state/payment/types';
import { isCreditCardExpired } from '@fhs-legacy/frontend/src/utils/payment';

import type { ISavedCardInfo, PaymentMethodType } from '../../../payment/payment-types';

export type PaymentMethodLabelAndIcon = {
  icon: ReactNode;
  label: string;
};

export type PaymentMethodRowDisplay = PaymentMethodLabelAndIcon & {
  subLabel?: string;
  expired?: boolean;
};

export const cardBrandIconMap: Record<CardBrand, ReactNode> = {
  [CardBrand.VISA]: <IconPaymentTypeVisa size={24} />,
  [CardBrand.MASTERCARD]: <IconPaymentTypeMastercard size={24} />,
  [CardBrand.AMEX]: <IconPaymentTypeAmex size={24} />,
  [CardBrand.DISCOVER]: <IconPaymentTypeDiscover size={24} />,
  [CardBrand.DINERS_CLUB]: <IconPaymentTypeDinersClub size={24} />,
  [CardBrand.JCB]: <IconPaymentTypeJcb size={24} />,
};

export const paymentMethodLabelAndIcon: Record<string, PaymentMethodLabelAndIcon> = {
  googlePay: {
    icon: <IconPaymentTypeGooglePay />,
    label: 'Google Pay',
  },
  applePay: {
    icon: <IconPaymentTypeApplePay size={24} />,
    label: 'Apple Pay',
  },
  giftCard: {
    icon: <IconRedeem size={24} />,
    label: 'Gift Card',
  },
  addNewGiftCard: {
    icon: <IconPaymentTypeAddNewPayment size={24} />,
    label: 'Add New Gift Card',
  },
  null: {
    icon: <IconPaymentTypeAddNewPayment size={24} />,
    label: 'Add New Credit Card',
  },
};

export function getGiftCardDisplay(lastFour: string): PaymentMethodLabelAndIcon {
  return {
    icon: <IconRedeem size={24} />,
    label: `Gift Card **** ${lastFour}`,
  };
}

export function getPaymentMethodDisplay(
  paymentMethod: PaymentMethodType,
  savedCardInfo?: ISavedCardInfo
): PaymentMethodLabelAndIcon {
  if (paymentMethod === 'savedCard' && savedCardInfo) {
    return getSavedCardDisplay(savedCardInfo.cardType, savedCardInfo.alias);
  }

  return paymentMethodLabelAndIcon[paymentMethod ?? 'null'] || paymentMethodLabelAndIcon.null;
}

function getSavedCardDisplay(cardType: CardBrand, lastFour: string): PaymentMethodLabelAndIcon {
  // displays saved card info obfuscated, can be used with gift cards
  return {
    icon: cardBrandIconMap[cardType] || <IconPaymentTypeGenericCreditCard size={24} />,
    label: `${cardType} **** ${lastFour}`,
  };
}

// Bridges the legacy `CardTypes` enum to the gateway `CardBrand` enum used by `cardBrandIconMap`.
// The values are identical strings except Diners Club (`DINERSCLUB` vs `DINERS_CLUB`).
function cardTypeToBrand(cardType: string): CardBrand {
  return cardType === CardTypes.DINERSCLUB
    ? CardBrand.DINERS_CLUB
    : (cardType as CardBrand);
}


export function getPaymentMethodRowDisplay(method: IPaymentMethod): PaymentMethodRowDisplay {
  const cardType = method.credit?.cardType;

  if (cardType === CardTypes.APPLE_PAY) {
    return paymentMethodLabelAndIcon.applePay;
  }
  if (cardType === CardTypes.GOOGLE_PAY) {
    return paymentMethodLabelAndIcon.googlePay;
  }
  if (method.credit) {
    const brand = cardType ? cardTypeToBrand(cardType) : undefined;
    const savedCardDisplay = brand
      ? getSavedCardDisplay(brand, method.credit.alias)
      : {
          icon: <IconPaymentTypeGenericCreditCard size={24} />,
          label: `**** ${method.credit.alias}`,
        };
    return {
      ...savedCardDisplay,
      subLabel: `exp. ${method.credit.expiryMonth}/${method.credit.expiryYear}`,
      expired: isCreditCardExpired(method.credit.expiryMonth, method.credit.expiryYear),
    };
  }
  if (method.giftCard) {
    return getGiftCardDisplay(method.giftCard.last4);
  }
  return { icon: <IconPaymentTypeGenericCreditCard size={24} />, label: 'Card' };
}
